<template>
  <SfMessageContainer :read="read" :has-opacity="todo && done">
    <template #top-left>
      <slot name="top-left"></slot>
    </template>
    <template #heading>
      <div class="tw-flex tw-gap-2 tw-items-center">
        <span
          ref="authorRef"
          v-tooltip="authorTooltip"
          class="
            tw-font-medium
            tw-text-sm
            tw-whitespace-nowrap
            tw-text-ellipsis
            tw-overflow-hidden
          "
          :class="hideSessionClass"
          data-testid="remark-card-flat-author-name"
        >
          <i v-if="createdByBot" class="i-bot"></i>
          {{ authorName }}
        </span>
        <time
          v-tooltip.timestamp="displayTime"
          class="tw-text-xs tw-text-sf-gray-light tw-whitespace-nowrap"
        >
          {{ displayTimeFormatted }}
        </time>
        <i
          v-if="hasCheckDiff"
          v-tooltip="I18n.t('communication.cards.hash_check_tooltip')"
          class="icon icon-info-circle"
        />
        <i
          v-if="hasDraft"
          v-tooltip="I18n.t('communication.cards.pending_draft_tooltip')"
          class="icon-edit"
        ></i>
        <span
          v-if="showPeriod && ledgerPeriodName"
          class="
            tw-px-2
            tw-py-1
            tw-font-normal
            tw-text-xs
            tw-border
            tw-rounded
            tw-border-solid
            tw-border-sf-neutral-light-1
            tw-whitespace-nowrap
          "
        >
          {{ ledgerPeriodName }}
        </span>
        <a
          v-if="allowedActions.openInContext && contextLink"
          ref="contextRef"
          v-tooltip="contextTooltip"
          :href="contextLink"
          class="
            tw-text-sm tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis
          "
          @click="trackClick('context-name')"
        >
          {{ contextText }}
        </a>
        <RemarkBubble
          v-if="pdfAnnotation"
          :id="'remark-' + id"
          :emit-click="true"
          :linking-path="pdfAnnotation.xfdfId"
          :prevent-bubble-unlink="preventBubbleUnlink"
          variant="default"
          @click.stop="gotoPdfAnnotation"
        />
        <RemarkBubble
          :id="`remark-${id}`"
          :linking-path="liquidInputFieldPath"
          :prevent-bubble-unlink="preventBubbleUnlink"
        />
      </div>
    </template>
    <template #before-message>
      <slot name="before-message"></slot>
    </template>
    <template #actions>
      <slot name="actions"></slot>
    </template>
    <template #body>
      <div
        v-dompurify-html="renderedText"
        class="remark-card-flat__rendered-text tw-text-sm tw-text-sf-gray"
        :class="messageClasses"
      ></div>
      <div class="tw-flex tw-flex-wrap tw-gap-1">
        <SfDocumentPill
          v-for="document in attachments"
          :id="document.id"
          :key="document.id"
          :viewing-url="document.url"
          :allow-delete="allowedActions.edit"
          @delete="emit('deleteAttachment', document.id)"
        >
          <template #left-icon>
            <i
              class="
                icon icon-file
                tw-text-xs tw-leading-4 tw-text-sf-gray-light
                hover:tw-text-sf-gray-light
              "
              data-testid="document-pill-file"
            ></i>
          </template>
          <template #default>
            <span :class="hideSessionClass">
              {{ document.filename }}
            </span>
          </template>
        </SfDocumentPill>
      </div>
      <div
        v-if="done && doneAt && doneByName"
        class="tw-uppercase tw-text-xs tw-font-medium tw-text-sf-dark-dark"
        :class="hideSessionClass"
      >
        {{ doneByText }}
      </div>
    </template>
  </SfMessageContainer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import RemarkBubble from '@app/application/communication/sections/communication/components/remarks/remark-bubble.vue'
import SfDocumentPill from '@app/shared/vue/components/sf-document-pill.vue'
import useDynamicTooltip from '@app/shared/vue/composables/use-dynamic-tooltip'
import useRedactSessionRecording from '@app/shared/vue/composables/use-hide-session-recording'
import useI18n from '@app/shared/vue/composables/use-I18n'
import communicationDate, {
  CommunicationDateFormats,
} from '@app/utils/communication-date'
import trackEvent from '@app/utils/track_event'
import SfMessageContainer from '../../sf-message/sf-message-container.vue'

const I18n = useI18n()
const hideSessionClass = useRedactSessionRecording()

const emit = defineEmits<{ deleteAttachment: [number]; visitAnnotation: [] }>()

interface Props {
  id: number
  showContext?: boolean
  contextText: string
  contextLink?: string
  ledgerPeriodName?: string
  liquidInputFieldPath?: string | null
  authorName: string
  createdByBot?: boolean
  createdAt: string
  editedAt?: string
  renderedText: string
  attachments: { url: string; filename: string; id: number }[]
  allowedActions: {
    edit: boolean
    openInContext: boolean
  }
  showPeriod?: boolean
  hasCheckDiff?: boolean
  hasDraft?: boolean
  pdfAnnotation?: {
    id: number
    xfdfId: string
    remarkId: number
    documentId: number
    userId: number
  }
  preventBubbleUnlink?: boolean
  read?: boolean
  done?: boolean
  doneAt?: string
  doneByName?: string
  todo?: boolean
  partOf?: string
}

const props = withDefaults(defineProps<Props>(), {
  contextLink: '',
  liquidInputFieldPath: null,
  ledgerPeriodName: '',
  editedAt: '',
  renderedText: '',
  attachments: () => [],
  doneAt: '',
  doneByName: '',
  partOf: '',
  pdfAnnotation: () => ({
    id: 0,
    xfdfId: '',
    remarkId: 0,
    documentId: 0,
    userId: 0,
  }),
})

const displayTime = computed(() => {
  return props.editedAt ? props.editedAt : props.createdAt
})

const displayTimeFormatted = computed(() => {
  return props.editedAt
    ? `${I18n.t('communication.cards.edited')} ${communicationDate(
        displayTime.value,
        CommunicationDateFormats.SHORT
      )}`
    : communicationDate(displayTime.value, CommunicationDateFormats.SHORT)
})

const doneByText = computed(() =>
  I18n.t('communication.cards.user_marked_as_done', { user: props.doneByName })
)

const messageClasses = computed(() => {
  return [hideSessionClass, { 'tw-font-medium': !props.read }]
})

const authorRef = ref<HTMLElement | null>(null)
const authorTooltip = useDynamicTooltip(authorRef, props.authorName)

const contextRef = ref<HTMLElement | null>(null)
const contextTooltip = useDynamicTooltip(contextRef, props.contextText)

function gotoPdfAnnotation() {
  emit('visitAnnotation')
}

function trackClick(label: string) {
  if (props.partOf) {
    trackEvent(props.partOf, 'click', label)
  }
}
</script>

<style lang="scss">
.remark-card-flat__rendered-text {
  word-break: break-word;

  blockquote {
    margin-bottom: 16px;
  }

  ul,
  ol {
    padding-left: 16px;
    margin-bottom: 16px;
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  &:last-child {
    margin-bottom: 0 !important; // Override for default bootstrap style
  }
}
</style>
